import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthProvider, AuthCallback } from '@/auth/AuthProvider';
import { Layout } from '@/components';
import { ChatProvider } from '@/context/ChatContext';
import {
  Dashboard,
  Sessions,
  SessionDetail,
  Vehicles,
  VehicleDetail,
  Reports,
  ReportViewer,
  ReportGenerate,
  Settings,
} from '@/pages';
import { trackPageView, analytics } from '@/utils/analytics';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Route change tracker for Google Analytics
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Get page name from pathname
    const getPageName = (pathname: string) => {
      if (pathname === '/') return 'Dashboard';
      if (pathname === '/sessions') return 'Sessions';
      if (pathname.startsWith('/sessions/')) return 'Session Detail';
      if (pathname === '/vehicles') return 'Vehicles';
      if (pathname.startsWith('/vehicles/')) return 'Vehicle Detail';
      if (pathname === '/reports') return 'Reports';
      if (pathname.startsWith('/reports/generate')) return 'Report Generate';
      if (pathname.startsWith('/reports/')) return 'Report Viewer';
      if (pathname === '/settings') return 'Settings';
      if (pathname === '/callback') return 'Auth Callback';
      return pathname;
    };

    trackPageView(getPageName(location.pathname));
  }, [location]);

  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();

  // Track login success
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      analytics.loginSuccess(user.email);
    }
  }, [isAuthenticated, user?.email]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-obdai-deep via-obdai-primary to-obdai-deep">
        <div className="text-center">
          <img
            src="/logo-icon-white.png"
            alt="OBDAI"
            className="h-16 mx-auto mb-8 animate-pulse"
          />
          <div className="w-48 h-1 bg-gray-700 rounded-full overflow-hidden mx-auto">
            <div className="h-full bg-gradient-to-r from-[#6d7cff] to-[#e866dd] rounded-full animate-[loading_1.5s_ease-in-out_infinite]" style={{ width: '40%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-obdai-deep via-obdai-primary to-obdai-deep">
        <div className="text-center max-w-lg px-8">
          {/* Logo - Big and commanding */}
          <img
            src="/logo-white.png"
            alt="OBDAI"
            className="h-24 mx-auto mb-12"
          />

          {/* Tagline */}
          <p className="text-gray-300 text-lg mb-12 leading-relaxed">
            Professional workspace for automotive diagnostics.
            <br />
            <span className="text-gray-400">Sign in to access your dashboard.</span>
          </p>

          {/* Premium Sign In Button */}
          <button
            onClick={() => loginWithRedirect()}
            className="w-full max-w-xs mx-auto px-8 py-4 bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white text-lg font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02]"
          >
            Sign In
          </button>

          {/* Secondary link */}
          <p className="text-gray-500 mt-10">
            Don't have an account?{' '}
            <a
              href="https://obdai.app/shop"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#6d7cff] hover:text-[#e866dd] font-medium transition-colors"
            >
              Get OBDAI
            </a>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <>
      <RouteTracker />
      <Routes>
        <Route path="/callback" element={<AuthCallback />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ChatProvider>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/sessions" element={<Sessions />} />
                <Route path="/sessions/:key" element={<SessionDetail />} />
                <Route path="/vehicles" element={<Vehicles />} />
                <Route path="/vehicles/:vin" element={<VehicleDetail />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/generate" element={<ReportGenerate />} />
                <Route path="/reports/:id" element={<ReportViewer />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
            </ChatProvider>
          </ProtectedRoute>
        }
      />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
